import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Composant d'autocomplétion d'adresses avec Google Places API
 * Remplit automatiquement les champs : adresse, code postal, ville, pays
 */
const AddressAutocomplete = ({
  onAddressSelect,
  label = 'Adresse complète',
  placeholder = 'Commencez à taper une adresse...',
  required = false,
  disabled = false,
  initialValue = '',
  addressFieldName = 'adresse',
  postalCodeFieldName = 'code_postal',
  cityFieldName = 'ville',
  countryFieldName = 'pays'
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Fonction de recherche avec debounce
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/autocomplete`, {
        input: query,
        session_token: sessionToken,
        language: 'fr',
        included_region_codes: ['fr', 'be', 'ch', 'de', 'es', 'it', 'gb']
      });

      setSuggestions(response.data.suggestions || []);
      setSessionToken(response.data.session_token);
      setIsOpen(true);
    } catch (error) {
      // Si l'API n'est pas configurée, désactiver silencieusement
      if (error.response?.status === 400 || error.response?.status === 500) {
      }
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  // Debounce la recherche
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Annuler le timer précédent
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Nouveau timer de 300ms
    const timer = setTimeout(() => {
      searchAddresses(value);
    }, 300);

    setDebounceTimer(timer);
  };

  // Sélection d'une adresse
  const handleSelectAddress = async (suggestion) => {
    if (!suggestion.place_id) {
      setInputValue(suggestion.text);
      setIsOpen(false);
      return;
    }

    try {
      // Récupérer les détails complets
      const response = await axios.post(`${BACKEND_URL}/api/place-details`, {
        place_id: suggestion.place_id,
        session_token: sessionToken
      });

      const details = response.data;
      const components = details.address_components || {};

      // Construire l'adresse complète
      const fullAddress = details.formatted_address || suggestion.text;
      setInputValue(fullAddress);

      // Callback avec tous les composants d'adresse
      if (onAddressSelect) {
        onAddressSelect({
          [addressFieldName]: components.street_name 
            ? `${components.street_number || ''} ${components.street_name}`.trim() 
            : fullAddress,
          [postalCodeFieldName]: components.postal_code || '',
          [cityFieldName]: components.city || '',
          [countryFieldName]: components.country || '',
          country_code: components.country_code || '',
          full_address: fullAddress,
          location: details.location
        });
      }

      // Réinitialiser la session
      setSessionToken(null);
      setIsOpen(false);
    } catch {
      setInputValue(suggestion.text);
      setIsOpen(false);
    }
  };

  // Nettoyage du timer au démontage
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <div className="space-y-2">
      <Label>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      <Popover open={isOpen && suggestions.length > 0} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={inputValue}
              onChange={handleInputChange}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              className="pl-10"
              onFocus={() => {
                if (suggestions.length > 0) {
                  setIsOpen(true);
                }
              }}
            />
          </div>
        </PopoverTrigger>
        
        {suggestions.length > 0 && (
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command>
              <CommandList>
                {loading ? (
                  <CommandEmpty>Recherche en cours...</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {suggestions.map((suggestion, index) => (
                      <CommandItem
                        key={index}
                        onSelect={() => handleSelectAddress(suggestion)}
                        className="cursor-pointer"
                      >
                        <MapPin className="mr-2 h-4 w-4" />
                        <div className="flex-1">
                          <div className="font-medium">{suggestion.text}</div>
                          {suggestion.structured_format && (
                            <div className="text-xs text-gray-500">
                              {suggestion.structured_format.secondary_text?.text || ''}
                            </div>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>

      <div className="text-xs text-gray-500">
        💡 Tapez au moins 3 caractères pour l'autocomplétion
      </div>
    </div>
  );
};

export default AddressAutocomplete;
